import React, { useEffect, useMemo, useState } from 'react';
import { isPreferenceSet } from '@nike/ux-tread-privacy';
import useBodega from '../../utils/hooks/bodegaHook';
import { BODEGA_DEFAULTS, COOKIE_MODAL_REJECT_ALL, COOKIE_SETTINGS } from '../../constants';
import templateString from '../../utils/templateString';
import PrivacyPolicyLink from './PrivacyPolicyLink';
import { getPrivacyCountry } from '../../../utils/privacy';
import isEnabled from '../../utils/settings';
import useLocale from '../../utils/hooks/localeHook';
import { SQCookie } from '../../../utils/legacyV2Cookies';
import { useNavOptionsContext } from '../../context/optionsContext';

export const usePreferences = () => {
  const [preferences, setPreferences] = useState({
    functional: true,
    allowPerformance: true,
    allowMarketing: true,
  });

  useEffect(() => {
    const { allowPerformance, allowMarketing } = SQCookie.get();
    setPreferences({
      functional: true,
      allowPerformance: allowPerformance ?? true,
      allowMarketing: allowMarketing ?? true,
    });
  }, []);

  return [preferences, setPreferences];
};

export const usePrivacyCountry = () => {
  const { country } = useLocale();
  const {
    opts: { useGeoPrivacy },
    mounted,
  } = useNavOptionsContext();

  return useMemo(
    () => getPrivacyCountry(country, useGeoPrivacy),
    [country, useGeoPrivacy, mounted],
  );
};

export const useIsRejectButtonEnabled = () => {
  const privacyCountry = usePrivacyCountry();
  return !!isEnabled({ country: privacyCountry }, COOKIE_MODAL_REJECT_ALL);
};

/**
 * Handles the logic for when to show the cookie modal
 * @param {Function} openModal function to open the modal
 */
export const useShouldShowCookieModal = openModal => {
  const {
    opts: { showPrivacyPolicy, useGeoPrivacy },
    mounted,
  } = useNavOptionsContext();
  const { country } = useLocale();
  const usePrivacyCountry = () =>
    useMemo(() => getPrivacyCountry(country, useGeoPrivacy), [country, useGeoPrivacy, mounted]);
  const privacyCountry = usePrivacyCountry();

  useEffect(() => {
    const shouldOpen =
      mounted &&
      !isPreferenceSet() &&
      showPrivacyPolicy > 0 &&
      !!isEnabled({ country: privacyCountry }, COOKIE_SETTINGS) &&
      !window.NikePrivacy.isEnabled;
    if (shouldOpen) {
      setTimeout(() => {
        openModal();
      }, showPrivacyPolicy);
    }
  }, [showPrivacyPolicy, privacyCountry, mounted]);
};

export const useBodegaStrings = () => ({
  title: useBodega('nav_cookie_label_yourCookieSettings', BODEGA_DEFAULTS.COOKIE_MODAL_TITLE),
  policyText: templateString(
    useBodega('nav_cookie_text_policy', BODEGA_DEFAULTS.COOKIE_MODAL_POLICY),
    {
      privacyAndCookiePolicyHTML: (
        <PrivacyPolicyLink key="cookie-modal-simple-privacy-policy-link" />
      ),
    },
  ),
  acceptButtonText: useBodega('nav_cookie_text_cookieAccept', BODEGA_DEFAULTS.COOKIE_MODAL_ACCEPT),
  rejectButtonText: useBodega('nav_cookie_text_cookieReject', BODEGA_DEFAULTS.COOKIE_MODAL_REJECT),
  moreInformationButtonText: useBodega(
    'nav_cookie_text_moreInformation',
    BODEGA_DEFAULTS.COOKIE_MODAL_MORE_INFO,
  ),
  doneButtonText: useBodega('nav_cookie_label_done', BODEGA_DEFAULTS.COOKIE_MODAL_DONE),
  footerText: templateString(
    useBodega('nav_cookie_text_footer', BODEGA_DEFAULTS.COOKIE_MODAL_FOOTER),
    {
      privacyAndCookiePolicyHTML: (
        <PrivacyPolicyLink key="cookie-modal-footer-privacy-policy-link" />
      ),
    },
  ),
});
